'use client';

import Image from 'next/image';
import {useTranslations} from 'next-intl';
import {type FC, memo, useMemo} from 'react';
import type {LeaderboardSharePreviewRow} from '@/entities/Actions/api/leaderboardShare';
import {ReferralLinkInput} from '@/entities/User/ui/UserInfo/ReferralLinkInput';
import {Modal} from '@/utils/antd-imports';

interface LeaderboardShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Full share copy from API (may contain \\n\\n). */
  shareText: string;
  referralLink: string;
  leaderboardPreview?: LeaderboardSharePreviewRow[];
}

const splitShareText = (shareText: string): {headline: string; body: string} => {
  const parts = shareText
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return {headline: '', body: ''};
  }
  if (parts.length === 1) {
    return {headline: parts[0] ?? '', body: ''};
  }
  return {
    headline: parts[0] ?? '',
    body: parts.slice(1).join('\n\n'),
  };
};

/**
 * @description Share modal for platform_share_leaderboard. Desktop sizes match Figma 2889-249485.
 */
export const LeaderboardShareModal: FC<LeaderboardShareModalProps> = memo(
  ({isOpen, onClose, shareText, referralLink, leaderboardPreview}) => {
    const t = useTranslations('experiences.card');
    const {headline, body} = useMemo(() => splitShareText(shareText), [shareText]);
    const previewRows = leaderboardPreview ?? [];
    const showPreview = previewRows.length > 0;

    return (
      <Modal
        open={isOpen}
        onCancel={onClose}
        footer={null}
        closeIcon={false}
        centered
        className="[&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-wrap]:!bg-[rgba(0,0,0,0.6)] [&_.ant-modal-content]:max-w-[288px] md:[&_.ant-modal-content]:max-w-[560px] [&_.ant-modal-content]:mx-auto [&_.ant-modal-content]:w-full"
        styles={{
          body: {padding: 0},
        }}
        width={'100%'}
      >
        {/* Figma .base: gap 24, px 32, py 48 (desktop) */}
        <div className="relative flex flex-col items-center gap-[16px] rounded-[24px] bg-[#1E1E2A] px-[24px] py-[28px] font-['Inter'] md:gap-[24px] md:px-[32px] md:py-[48px]">
          {/* Figma btn_close: right/top 16, p 8, icon 20 */}
          <button
            type="button"
            onClick={onClose}
            className="absolute right-[12px] top-[12px] flex items-center justify-center rounded-full border border-[rgba(233,236,240,0.2)] p-[8px] transition-opacity hover:opacity-[0.8] md:right-[16px] md:top-[16px]"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <title>Close</title>
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#E9ECF0"
                strokeWidth="1.35"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Figma header: px 48 (desktop) */}
          <div className="flex w-full flex-col items-center gap-[8px] px-[24px] pt-[4px] md:gap-0 md:px-[48px] md:pt-0">
            {headline ? (
              <h2 className="text-center font-['inter'] text-[20px] font-bold leading-[28px] text-[#E9ECF0] md:text-[36px] md:leading-[normal]">
                {headline}
              </h2>
            ) : null}
            {body ? (
              <p className="whitespace-pre-line text-center font-['inter'] text-[14px] font-medium leading-[20px] text-[rgba(233,236,240,0.75)] md:mt-[12px] md:text-[16px] md:leading-[normal]">
                {body}
              </p>
            ) : null}
          </div>

          {/* Figma body: w 406, gap 32 between cohort and referral block */}
          <div className="flex w-full flex-col gap-[20px] md:max-w-[406px] md:gap-[32px]">
            {showPreview && (
              /* Figma Leaderboard/Cohort: p 16, rounded 16; inner column gap 8 */
              <div className="flex w-full flex-col gap-[4px] rounded-[16px] bg-[rgba(49,50,75,0.35)] p-[8px] md:gap-[8px] md:p-[16px]">
                {previewRows.map((row, index) => (
                  <div key={`${row.rank}-${row.displayName}-${index}`} className="flex w-full items-center md:h-[48px]">
                    <div
                      className="flex w-full flex-1 flex-col justify-center rounded-[8px] p-[6px] py-[8px] md:h-full md:rounded-[10px] md:p-[8px]"
                      style={
                        row.isCurrentUser
                          ? {
                              background: 'linear-gradient(35.48deg, #D93C14 2.52%, #FF7A00 93.65%)',
                            }
                          : {background: 'rgba(49,50,75,0.6)'}
                      }
                    >
                      <div className="flex w-full items-center justify-between gap-[8px]">
                        <div className="flex min-w-0 flex-1 items-center gap-[8px] md:gap-[12px]">
                          <div className="flex h-[20px] min-w-[28px] shrink-0 items-center justify-center rounded-[6px] bg-[rgba(0,0,0,0.25)] px-[8px] py-[2px] md:h-[28px] md:min-w-[38px] md:w-[38px] md:rounded-[8px] md:px-[8px] md:py-[4px]">
                            <span
                              className={`text-center text-[12px] font-bold leading-[1.5] md:text-[15px] ${
                                row.isCurrentUser ? 'text-[#FFFFFF]' : 'text-[#b2b4cf]'
                              }`}
                            >
                              {row.rank}
                            </span>
                          </div>
                          <span
                            className={`truncate text-[12px] leading-[1.3] md:text-[16px] md:leading-[1.3] ${
                              row.isCurrentUser ? 'font-bold text-[#E9ECF0]' : 'font-normal text-[#E9ECF0]'
                            }`}
                          >
                            {row.displayName}
                          </span>
                        </div>
                        {typeof row.score === 'number' ? (
                          <div
                            className={`flex h-[32px] shrink-0 items-center gap-[4px] rounded-full px-[8px] py-[4px] md:h-[36px] md:px-[10px] md:py-[4px] ${
                              row.isCurrentUser ? 'bg-[#1a1a1a]' : 'bg-[rgba(0,0,0,0.25)]'
                            }`}
                          >
                            <Image
                              src="/images/actions/coin.png"
                              alt=""
                              width={20}
                              height={20}
                              className="size-[18px] md:size-[20px]"
                            />
                            <span className="text-center text-[16px] font-semibold leading-[1.3] text-[#E9ECF0] md:text-[20px]">
                              {row.score}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Figma: w 336, gap 16; help text 16px / leading 20 */}
            <div className="mx-auto flex w-full max-w-full flex-col items-center gap-[16px] md:max-w-[336px]">
              <p className="w-full text-center font-['inter'] text-[16px] font-medium leading-[20px] text-[rgba(233,236,240,0.8)] md:leading-[20px]">
                {t('leaderboard_share_help_referral')}
              </p>
              <ReferralLinkInput
                className="mx-auto w-full justify-center"
                initialLink={referralLink}
                shareTitle={shareText}
                variant="socialOnly"
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  },
);

LeaderboardShareModal.displayName = 'LeaderboardShareModal';
